import { type AnyHTMLInputElementType, isStringLiteralOfType, exhaustiveGuard } from "@mcwd/typescript-type-guards";
import { FormInputTagName, HTMLInputElementTypeAttributeValue, type HtmlInputTypeName, type HtmlInputTypeNameToHTMLElement } from "../../../types-and-definitions/forms/input-element.js";

function isAnyInputElement (value: HTMLElement): value is AnyHTMLInputElementType {
  return value.matches("input,select,textarea");
}

type GetSelectorFromInputType<T extends FormInputTagName | HTMLInputElementTypeAttributeValue> = 
  T extends FormInputTagName
    ? T
    :
  T extends HTMLInputElementTypeAttributeValue
    ? `input[type=${T}]`
    : never;

function getSelectorFromInputType<const TInputType extends FormInputTagName | HtmlInputTypeName>(
  inputType: TInputType
): GetSelectorFromInputType<TInputType>
{
  type ThisReturnType = GetSelectorFromInputType<TInputType>;
  if (isStringLiteralOfType(inputType, FormInputTagName)){
    return inputType as ThisReturnType;
  }
  else if (isStringLiteralOfType(inputType, HTMLInputElementTypeAttributeValue)){
    return `input[type='${ inputType }']` as ThisReturnType;
  }
  exhaustiveGuard(inputType);
}

export function getInputElementArray<const TInputType extends FormInputTagName | HtmlInputTypeName, TAllowHiddenInputDefault extends boolean = true> ({
  selector, inputType, parentNode = window.document, allowHiddenInputDefault
}: {
  selector: string;
  inputType: TInputType;
  parentNode: ParentNode;
  allowHiddenInputDefault?: TAllowHiddenInputDefault
}): (HtmlInputTypeNameToHTMLElement<TInputType> | ([TAllowHiddenInputDefault] extends [true]? HTMLInputElement : never))[]
{
  type ThisReturnType = HtmlInputTypeNameToHTMLElement<TInputType>[];

  const allInputsWithSelector : AnyHTMLInputElementType[] = [ ...parentNode.querySelectorAll<HTMLElement>(selector) ].filter(isAnyInputElement);

  const expectedMatchingInputSelector = getSelectorFromInputType(inputType);

  const inputsWithInputType: (HtmlInputTypeNameToHTMLElement<TInputType> | ([ TAllowHiddenInputDefault ] extends [ true ] ? HTMLInputElement : never))[] = [];
  const inputsNotMatchingType: AnyHTMLInputElementType[] = [];

  for (const el of allInputsWithSelector){
    if (el.matches(expectedMatchingInputSelector)){
      inputsWithInputType.push(el as HtmlInputTypeNameToHTMLElement<TInputType>);
    }
    // eslint-disable-next-line no-negated-condition
    else if (allowHiddenInputDefault !== false)
    {
      inputsWithInputType.push(el as ([ TAllowHiddenInputDefault ] extends [ true ] ? HTMLInputElement : never));
    }
    else {
      inputsNotMatchingType.push(el);
    }
  }
  if (inputsNotMatchingType.length > 0){
    const errors = inputsNotMatchingType.map(el => {
      return new Error(`Input element with selector '${ selector }' does not match the expected type '${inputType}'! Type: '${ Object.prototype.toString.call(el) }'`, { cause: el });
    });
    if (errors.length === 1){
      throw errors[0];
    }
    else {
      throw new AggregateError(errors, `Unexpected input types encountered!`);
    }
  }

  return inputsWithInputType as ThisReturnType;
}


export function getInputElement<const TInputType extends FormInputTagName | HtmlInputTypeName, TAllowHiddenInputDefault extends boolean = true> ({
  selector, inputType, parentNode = window.document, allowHiddenInputDefault
}: {
  selector: string;
  inputType: TInputType;
  parentNode: ParentNode;
  allowHiddenInputDefault?: TAllowHiddenInputDefault;
}): HtmlInputTypeNameToHTMLElement<TInputType> | null
{
  type ThisReturnType = HtmlInputTypeNameToHTMLElement<TInputType> | null;
  const matchingInputElements = getInputElementArray({ selector, inputType, parentNode, allowHiddenInputDefault: allowHiddenInputDefault !== false });

  if (matchingInputElements.length === 0) {
    return null;
  }
  if (matchingInputElements.length > 1) {
    throw new Error(`Found too many inputs elements matching the selector '${selector}'`, { cause: matchingInputElements });
  }

  return matchingInputElements[ 0 ] as ThisReturnType;
}